import { TableProperties } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const Pill = ({ score, meta }) => {
  const m = meta[score];
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-[11px] font-bold leading-tight ${m.cls}`}>
      {m.label}
    </span>
  );
};

export const ComparisonMatrix = () => {
  const { d, ui } = useLang();
  const CRITERIA = d.CRITERIA;
  const THEORIES = d.THEORIES;
  const SCORE_META = d.SCORE_META;

  return (
    <section id="matrix" data-testid="matrix-section" className="scroll-mt-24 border-b border-line py-20">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-10 flex items-start gap-4">
          <div className="rounded-full bg-anthra p-3 text-paper">
            <TableProperties className="h-5 w-5" />
          </div>
          <div>
            <p className="eyebrow">{ui.matrixEyebrow}</p>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl font-semibold tracking-tight text-ink">
              {ui.matrixTitle}
            </h2>
            <p className="mt-3 max-w-3xl text-base md:text-lg leading-relaxed text-muted-foreground">
              {ui.matrixDesc}
            </p>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="matrix-legend">
          {Object.values(SCORE_META).map((m) => (
            <span key={m.label} className={`rounded-full px-3 py-1 text-[11px] font-bold ${m.cls}`}>
              {m.label}
            </span>
          ))}
        </div>

        {/* Mobile: κάρτες ανά κριτήριο — χωρίς οριζόντια κύλιση */}
        <div className="space-y-4 md:hidden" data-testid="matrix-cards">
          {CRITERIA.map((row, i) => (
            <div key={row.criterion} className="rounded-lg border border-line bg-paper p-5">
              <p className="font-serif text-lg font-semibold text-ink">{row.criterion}</p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-terra">{row.source}</p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{row.question}</p>
              <div className="mt-4 space-y-4">
                {THEORIES.map((t) => {
                  const cell = row.cells[t.key];
                  return (
                    <div key={t.key} className="border-t border-line pt-3" data-testid={`matrix-mcell-${i}-${t.key}`}>
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-ink/60">{t.name}</p>
                      <div className="mt-1.5"><Pill score={cell.score} meta={SCORE_META} /></div>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">{cell.note}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: πλήρης πίνακας */}
        <div className="hidden overflow-x-auto rounded-lg border border-line md:block" data-testid="matrix-table">
          <table className="w-full min-w-[880px] border-collapse text-left">
            <thead>
              <tr className="bg-aegean text-paper">
                <th className="w-[26%] px-5 py-4 text-xs font-bold uppercase tracking-[0.15em]">{ui.matrixCriterionCol}</th>
                {THEORIES.map((t) => (
                  <th key={t.key} className="px-5 py-4">
                    <span className="block text-sm font-bold">{t.name}</span>
                    <span className="block text-[11px] font-normal text-paper/70">{t.sub}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CRITERIA.map((row, i) => (
                <tr key={row.criterion} className={i % 2 === 0 ? 'bg-paper' : 'bg-surface/60'}>
                  <td className="align-top px-5 py-5">
                    <p className="font-serif text-base md:text-lg font-semibold text-ink">{row.criterion}</p>
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-terra">{row.source}</p>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{row.question}</p>
                  </td>
                  {THEORIES.map((t) => {
                    const cell = row.cells[t.key];
                    return (
                      <td key={t.key} className="align-top px-5 py-5" data-testid={`matrix-cell-${i}-${t.key}`}>
                        <Pill score={cell.score} meta={SCORE_META} />
                        <p className="mt-2 text-sm leading-relaxed text-foreground/85">{cell.note}</p>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};
